import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
  LineChart, Line,
} from "recharts";
import { Package, AlertTriangle, Truck, Send, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, ShoppingCart, Sparkles, Pencil, Download, TrendingUp, TrendingDown } from "lucide-react";

const CHART_COLORS = ["#B46B52", "#D9A05B", "#7B8B6F", "#8B5A2B", "#1E6B8A"];

const CATEGORY_META = {
  ordine: { label: "Ordine consegnato", icon: ShoppingCart, color: "#7B8B6F", bg: "rgba(123,139,111,0.15)" },
  trasferimento: { label: "Trasferimento", icon: ArrowLeftRight, color: "#1E6B8A", bg: "rgba(30,107,138,0.15)" },
  uscita: { label: "Scarico in uscita", icon: ArrowUpCircle, color: "#8B3A36", bg: "rgba(139,58,54,0.12)" },
  iniziale: { label: "Carico iniziale", icon: Sparkles, color: "#D9A05B", bg: "rgba(217,160,91,0.18)" },
  manuale: { label: "Modifica manuale", icon: Pencil, color: "#8A7358", bg: "rgba(138,115,88,0.15)" },
};

function _dateBucket(iso) {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  const dn = new Date(d);
  dn.setHours(0, 0, 0, 0);
  if (dn.getTime() === today.getTime()) return "Oggi";
  if (dn.getTime() === yest.getTime()) return "Ieri";
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

function StatCard({ icon: Icon, label, value, hint, accent, testid, to }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="label-tag">{label}</div>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: accent || "rgba(180,107,82,0.1)", color: accent ? "white" : "var(--ds-primary)" }}
        >
          <Icon size={18} strokeWidth={1.8} />
        </div>
      </div>
      <div className="data-number mt-4">{value}</div>
      {hint && (
        <div className="text-xs mt-1" style={{ color: "var(--ds-text-secondary)" }}>
          {hint}
        </div>
      )}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="ds-card p-6 block transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2"
        style={{ cursor: "pointer" }}
        data-testid={testid}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="ds-card p-6" data-testid={testid}>
      {inner}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [valoreStorico, setValoreStorico] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, v, pc] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/valore-storico"),
          api.get("/price-changes?limit=20"),
        ]);
        setStats(s.data);
        setValoreStorico(v.data || []);
        setPriceChanges(pc.data || []);
      } catch (e) {
        toast.error("Errore caricamento dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="font-brand text-2xl" style={{ color: "var(--ds-primary)" }}>Caricamento dashboard…</div>;
  }
  if (!stats) {
    return <div>Errore nel caricamento</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-tag mb-2">Panoramica</div>
          <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">
            Buongiorno.
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
            Ecco l'andamento attuale del tuo magazzino.
          </p>
        </div>
        <div className="ds-card px-5 py-3">
          <div className="label-tag">Valore magazzino</div>
          <div className="data-number" data-testid="dashboard-total-value">
            € {stats.valore_totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Package}
          label="Giacenza totale"
          value={stats.giacenza_totale.toLocaleString("it-IT")}
          hint={`${stats.totale_prodotti} prodotti in catalogo`}
          testid="kpi-giacenza"
          to="/prodotti"
        />
        <StatCard
          icon={AlertTriangle}
          label="In esaurimento"
          value={stats.in_esaurimento}
          hint="sotto soglia minima"
          accent={stats.in_esaurimento > 0 ? "#8B3A36" : null}
          testid="kpi-esaurimento"
          to="/prodotti?filter=esaurimento"
        />
        <StatCard
          icon={Send}
          label="Ordini da inviare"
          value={stats.ordini_da_inviare}
          hint="da elaborare"
          testid="kpi-ordini-inviare"
          to="/ordini?stato=da_inviare"
        />
        <StatCard
          icon={Truck}
          label="Ordini in consegna"
          value={stats.ordini_in_consegna}
          hint="in viaggio"
          testid="kpi-ordini-consegna"
          to="/ordini?stato=in_consegna"
        />
      </div>

      {/* Chart row */}
      <div className="ds-card p-6" data-testid="dashboard-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tag">Andamento</div>
            <h3 className="font-primary text-xl font-medium mt-1">Valore magazzino nel tempo</h3>
            <p className="text-xs mt-1" style={{ color: "var(--ds-text-secondary)" }}>
              Valore ricostruito dai movimenti: quantità × prezzo di acquisto
            </p>
          </div>
          {stats.valore_per_deposito.length > 0 && (
            <div className="text-right">
              <div className="label-tag">Per deposito</div>
              <div className="text-xs mt-1 space-x-2" style={{ color: "var(--ds-text-secondary)" }}>
                {stats.valore_per_deposito.slice(0, 4).map((d, i) => (
                  <span key={d.deposito} className="inline-block">
                    <span style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}>●</span>
                    {" "}{d.deposito}: <b>€ {d.valore.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {valoreStorico.length < 2 ? (
          <div className="h-64 flex items-center justify-center text-sm text-center" style={{ color: "var(--ds-text-secondary)" }}>
            Storico insufficiente. Il valore viene tracciato ad ogni movimento di carico/scarico.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={valoreStorico} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D9" vertical={false} />
              <XAxis
                dataKey="data"
                tick={{ fill: "#8A7358", fontSize: 12 }}
                axisLine={{ stroke: "#E8E1D9" }}
                tickFormatter={(d) => new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
              />
              <YAxis
                tick={{ fill: "#8A7358", fontSize: 12 }}
                axisLine={{ stroke: "#E8E1D9" }}
                tickFormatter={(v) => `€ ${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`}
              />
              <Tooltip
                contentStyle={{
                  background: "#FFFFFF",
                  border: "1px solid #E8E1D9",
                  borderRadius: 10,
                  color: "#3D2B1F",
                }}
                formatter={(v) => [`€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`, "Valore"]}
                labelFormatter={(d) => new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
              />
              <Line type="monotone" dataKey="valore" stroke="#B46B52" strokeWidth={2.5} dot={{ r: 3, fill: "#B46B52" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Price changes */}
      <PriceChangesCard items={priceChanges} />

      {/* Timeline */}
      <TimelineCard movements={stats.movimenti_recenti} />
    </div>
  );
}

function PriceChangesCard({ items }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="ds-card p-6" data-testid="dashboard-price-changes">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">Listino</div>
          <h3 className="font-primary text-xl font-medium mt-1">Variazioni prezzo recenti</h3>
          <p className="text-xs mt-1" style={{ color: "var(--ds-text-secondary)" }}>
            Aggiornate dalle fatture importate, dal più recente al più vecchio.
          </p>
        </div>
        <Link
          to="/fatture"
          className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors hover:bg-[var(--ds-bg)]"
          style={{ borderColor: "var(--ds-primary)", color: "var(--ds-primary)" }}
          data-testid="dashboard-import-invoice"
        >
          Importa fattura →
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="text-sm py-8 text-center" style={{ color: "var(--ds-text-secondary)" }}>
          Nessuna variazione di prezzo registrata. Importa una fattura per iniziare a tracciare le variazioni.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--ds-border)" }}>
                <th className="text-left py-2 px-2 label-tag">Prodotto</th>
                <th className="text-right py-2 px-2 label-tag">Vecchio prezzo</th>
                <th className="text-right py-2 px-2 label-tag">Nuovo prezzo</th>
                <th className="text-right py-2 px-2 label-tag">Variazione</th>
                <th className="text-right py-2 px-2 label-tag hidden md:table-cell">Data</th>
                <th className="text-left py-2 px-2 label-tag hidden lg:table-cell">Fonte</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const v = Number(c.variazione_perc || 0);
                const up = v >= 0;
                return (
                  <tr key={c.id} className="border-b hover:bg-[var(--ds-bg)]" style={{ borderColor: "var(--ds-border)" }} data-testid={`price-change-${c.id}`}>
                    <td className="py-2 px-2">
                      <Link to={`/prodotti/${c.prodotto_id}`} className="font-medium hover:underline" style={{ color: "var(--ds-text-primary)" }}>
                        {c.prodotto_nome}
                      </Link>
                      <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>{c.fornitore_nome || ""}</div>
                    </td>
                    <td className="py-2 px-2 text-right">€ {fmt(c.vecchio_prezzo)}</td>
                    <td className="py-2 px-2 text-right font-medium">€ {fmt(c.nuovo_prezzo)}</td>
                    <td className="py-2 px-2 text-right">
                      <span
                        className="inline-flex items-center gap-1 font-medium"
                        style={{ color: up ? "var(--ds-danger)" : "#15803D" }}
                      >
                        {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {up ? "+" : ""}{v.toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-xs hidden md:table-cell" style={{ color: "var(--ds-text-secondary)" }}>
                      {fmtDate(c.created_at)}
                    </td>
                    <td className="py-2 px-2 text-xs hidden lg:table-cell" style={{ color: "var(--ds-text-secondary)" }}>
                      {c.fonte === "fattura" ? `Fattura ${c.fattura_numero || ""}` : c.fonte}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TimelineCard({ movements }) {
  const [filter, setFilter] = useState("tutti");
  const [selected, setSelected] = useState(null);

  const categories = ["tutti", "ordine", "trasferimento", "uscita", "manuale", "iniziale"];
  const counts = movements.reduce((acc, m) => {
    acc[m.categoria] = (acc[m.categoria] || 0) + 1;
    return acc;
  }, {});

  const filtered = filter === "tutti" ? movements : movements.filter((m) => m.categoria === filter);

  // Group by date bucket preserving order
  const groups = [];
  let lastBucket = null;
  filtered.forEach((m) => {
    const b = _dateBucket(m.created_at);
    if (b !== lastBucket) {
      groups.push({ bucket: b, items: [] });
      lastBucket = b;
    }
    groups[groups.length - 1].items.push(m);
  });

  return (
    <div className="ds-card p-6" data-testid="dashboard-timeline">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">Timeline</div>
          <h3 className="font-primary text-xl font-medium mt-1">Diario del magazzino</h3>
          <p className="text-xs mt-1" style={{ color: "var(--ds-text-secondary)" }}>
            Ultimi {movements.length} eventi: ordini consegnati, scarichi, trasferimenti.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {categories.map((c) => {
            const active = filter === c;
            const meta = c === "tutti" ? null : CATEGORY_META[c];
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`timeline-filter-${c}`}
                className="text-xs px-3 py-1.5 rounded-full transition-colors border"
                style={{
                  background: active ? (meta?.bg || "var(--ds-text-primary)") : "transparent",
                  color: active ? (meta?.color || "white") : "var(--ds-text-secondary)",
                  borderColor: active ? (meta?.color || "var(--ds-text-primary)") : "var(--ds-border)",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {c === "tutti" ? "Tutti" : meta.label}
                <span className="ml-1.5 opacity-70">
                  {c === "tutti" ? movements.length : counts[c] || 0}
                </span>
              </button>
            );
          })}
          <ExportButton categoria={filter === "tutti" ? null : filter} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm py-10 text-center" style={{ color: "var(--ds-text-secondary)" }}>
          Nessun evento da mostrare.
        </div>
      ) : (
        <div className="space-y-6 max-h-[520px] overflow-y-auto pr-2 pl-2">
          {groups.map((g) => (
            <div key={g.bucket}>
              <div className="label-tag mb-3 sticky top-0 bg-white py-1 -mx-1 px-1 z-10">{g.bucket}</div>
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: "var(--ds-border)" }}></div>
                {g.items.map((m) => {
                  const meta = CATEGORY_META[m.categoria] || CATEGORY_META.manuale;
                  const Icon = meta.icon;
                  const isCarico = m.tipo === "carico";
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelected(m)}
                      className="relative w-full text-left flex items-start gap-3 pb-4 rounded-lg hover:bg-[var(--ds-bg)] transition-colors px-2 -mx-2 py-1"
                      data-testid={`timeline-item-${m.id}`}
                    >
                      <div
                        className="absolute -left-[12px] top-1 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white shrink-0"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        <Icon size={13} strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0 pl-4">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-xs uppercase tracking-wider font-medium" style={{ color: meta.color }}>
                            {meta.label}
                          </span>
                          <span className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                            · {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <div className="font-medium text-sm mt-0.5 truncate">{m.prodotto_nome}</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--ds-text-secondary)" }}>
                          {m.deposito_nome}
                          {m.utente_nome && ` · ${m.utente_nome}`}
                          {m.note && ` · ${m.note}`}
                        </div>
                      </div>
                      <div
                        className="font-primary font-semibold text-sm shrink-0 flex items-center gap-1"
                        style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}
                      >
                        {isCarico ? <ArrowDownCircle size={14} /> : <ArrowUpCircle size={14} />}
                        {isCarico ? "+" : "−"}{m.quantita}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <TimelineDetailSheet event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function TimelineDetailSheet({ event, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!event) return;
    setOrder(null);
    if (event.categoria === "ordine") {
      // parse "Ordine ORD-XXXX consegnato"
      const m = (event.note || "").match(/Ordine\s+(ORD-\d+)/i);
      if (m) {
        setLoading(true);
        api.get("/orders").then(({ data }) => {
          setOrder(data.find((o) => o.numero === m[1]) || null);
        }).catch(() => {}).finally(() => setLoading(false));
      }
    }
  }, [event]);

  if (!event) return null;
  const meta = CATEGORY_META[event.categoria] || CATEGORY_META.manuale;
  const Icon = meta.icon;
  const isCarico = event.tipo === "carico";
  // For trasferimento, parse the two deposit names from note "Trasferimento da X a Y"
  let transferDeposits = null;
  if (event.categoria === "trasferimento") {
    const m = (event.note || "").match(/Trasferimento da\s+(.+?)\s+a\s+(.+)/i);
    if (m) transferDeposits = { da: m[1], a: m[2] };
  }

  return (
    <Sheet open={!!event} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: meta.bg, color: meta.color }}
            >
              <Icon size={18} strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-medium" style={{ color: meta.color }}>
                {meta.label}
              </div>
              <SheetTitle className="font-brand text-2xl font-light leading-tight">
                Dettaglio movimento
              </SheetTitle>
            </div>
          </div>
          <SheetDescription className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
            {new Date(event.created_at).toLocaleString("it-IT", {
              dateStyle: "full", timeStyle: "short",
            })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Common: product + qty */}
          <div className="ds-card p-4">
            <div className="label-tag">Prodotto</div>
            <div className="font-medium mt-1">{event.prodotto_nome}</div>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <div className="label-tag">Tipo</div>
                <div className="text-sm mt-0.5" style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                  {isCarico ? "Carico" : "Scarico"}
                </div>
              </div>
              <div className="text-right">
                <div className="label-tag">Quantità</div>
                <div className="font-primary text-xl font-semibold" style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                  {isCarico ? "+" : "−"}{event.quantita}
                </div>
              </div>
            </div>
          </div>

          {/* Category specific */}
          {event.categoria === "trasferimento" && transferDeposits && (
            <div className="ds-card p-4">
              <div className="label-tag mb-3">Magazzini coinvolti</div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                  <div className="label-tag">Da</div>
                  <div className="font-medium mt-0.5">{transferDeposits.da}</div>
                </div>
                <ArrowLeftRight size={18} style={{ color: "var(--ds-primary)" }} />
                <div className="flex-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                  <div className="label-tag">A</div>
                  <div className="font-medium mt-0.5">{transferDeposits.a}</div>
                </div>
              </div>
            </div>
          )}

          {event.categoria !== "trasferimento" && (
            <div className="ds-card p-4">
              <div className="label-tag">Magazzino</div>
              <div className="font-medium mt-1">{event.deposito_nome}</div>
            </div>
          )}

          {event.note && (
            <div className="ds-card p-4">
              <div className="label-tag">Nota</div>
              <div className="text-sm mt-1" style={{ color: "var(--ds-text-secondary)" }}>{event.note}</div>
            </div>
          )}

          {event.utente_nome && (
            <div className="ds-card p-4">
              <div className="label-tag">Eseguito da</div>
              <div className="text-sm mt-1 font-medium">{event.utente_nome}</div>
            </div>
          )}

          {/* Order details if it's an order event */}
          {event.categoria === "ordine" && (
            <div className="ds-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tag">Ordine collegato</div>
                {order && (
                  <Link
                    to="/ordini"
                    onClick={onClose}
                    className="text-xs hover:underline"
                    style={{ color: "var(--ds-primary)" }}
                    data-testid="timeline-open-order"
                  >
                    Apri ordini →
                  </Link>
                )}
              </div>
              {loading ? (
                <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>Caricamento…</div>
              ) : order ? (
                <>
                  <div className="font-mono font-semibold text-base">{order.numero}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--ds-text-secondary)" }}>
                    Totale € {Number(order.totale || 0).toFixed(2)} · {order.righe?.length || 0} righe
                  </div>
                  <div className="mt-3 space-y-1.5 border-t pt-3" style={{ borderColor: "var(--ds-border)" }}>
                    {(order.righe || []).map((r, i) => (
                      <div key={r.prodotto_id || `row-${i}`} className="flex items-center justify-between text-xs">
                        <span className="truncate flex-1">{r.nome_articolo}</span>
                        <span className="mx-2 font-medium">×{r.quantita}</span>
                        <span style={{ color: "var(--ds-text-secondary)" }}>€ {Number(r.totale_riga).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
                  Ordine non più disponibile.
                </div>
              )}
            </div>
          )}

          {/* Quick link to product */}
          {event.prodotto_id && event.categoria !== "ordine" && (
            <Link
              to={`/prodotti/${event.prodotto_id}`}
              onClick={onClose}
              className="block ds-card p-4 hover:bg-[var(--ds-bg)] transition-colors"
              data-testid="timeline-open-product"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-tag">Apri scheda prodotto</div>
                  <div className="text-sm mt-1 font-medium" style={{ color: "var(--ds-primary)" }}>
                    {event.prodotto_nome} →
                  </div>
                </div>
                <Package size={20} style={{ color: "var(--ds-primary)" }} />
              </div>
            </Link>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ExportButton({ categoria }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(fmt(firstOfMonth));
  const [to, setTo] = useState(fmt(today));
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (from && to && from > to) {
      toast.error("La data 'Da' non può essere successiva a 'A'");
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (categoria) params.set("categoria", categoria);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/movements/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `movimenti_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export scaricato");
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Errore export");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors hover:bg-[var(--ds-bg)]"
          style={{ borderColor: "var(--ds-primary)", color: "var(--ds-primary)" }}
          data-testid="timeline-export-button"
        >
          <Download size={12} /> Esporta CSV
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4">
        <div className="space-y-3">
          <div>
            <div className="label-tag mb-1">Esporta movimenti</div>
            <p className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
              Scarica un CSV (separatore ; — compatibile Excel italiano)
              {categoria && (
                <> · filtro categoria: <b>{categoria}</b></>
              )}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-tag">Da</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 rounded-lg text-sm"
                data-testid="export-date-from"
              />
            </div>
            <div>
              <Label className="label-tag">A</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 rounded-lg text-sm"
                data-testid="export-date-to"
              />
            </div>
          </div>
          <Button
            onClick={download}
            disabled={busy}
            className="w-full ds-btn-primary rounded-lg"
            style={{ background: "var(--ds-primary)" }}
            data-testid="export-download"
          >
            <Download size={14} className="mr-1.5" />
            {busy ? "Generazione…" : "Scarica CSV"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

